import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import Login from "./components/Login";
import RHHomepage from "./components/RHHomepage";
import ManagerHomepage from "./components/ManagerHomepage";
import ManagerHorizonHomepage from "./components/ManagerHorizonHomepage";
import RecrutementsGeneraux from "./components/RecrutementsGeneraux";
import JobOfferDetailPage from "./components/JobOfferDetailPage";
import VivierCandidatures from "./components/VivierCandidatures";
import ManagerOffresEnCours from "./components/ManagerOffresEnCours";
import ManagerOfferDetailPage from "./components/ManagerOfferDetailPage";
import ManagerOffresEnAttente from "./components/ManagerOffresEnAttente";
import RHOffresArchivees from "./components/RHOffresArchivees";
import ManagerOffresArchivees from "./components/ManagerOffresArchivees";
import RHArchivedOfferDetailPage from "./components/RHArchivedOfferDetailPage";
import ManagerArchivedOfferDetailPage from "./components/ManagerArchivedOfferDetailPage";
import ManagerHSOffresEnCours from "./components/ManagerHSOffresEnCours";
import ManagerHSOfferDetailPage from "./components/ManagerHSOfferDetailPage";
import ManagerHSOffresEnAttente from "./components/ManagerHSOffresEnAttente";
import RHHorizonSolution from "./components/RHHorizonSolution";
import RHHSOfferDetailPage from "./components/RHHSOfferDetailPage";
import GestionComptes from "./components/GestionComptes";
import AdminHomepage from "./components/AdminHomepage";
import AdminOffresEnCours from "./components/AdminOffresEnCours";
import AdminOffresEnAttente from "./components/AdminOffresEnAttente";
import AdminOffresArchivees from "./components/AdminOffresArchivees";
import AdminOfferDetailPage from "./components/AdminOfferDetailPage";
import AdminArchivedOfferDetailPage from "./components/AdminArchivedOfferDetailPage";
import { Toaster } from "./components/ui/sonner";
import { useSSO } from "./hooks/useSSO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Gérer les cas où REACT_APP_BACKEND_URL contient déjà /api
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user info
      fetch(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Token invalid');
      })
      .then(userData => {
        setUser(userData);
      })
      .catch(() => {
        localStorage.removeItem('token');
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    // Si appelé avec un objet user (depuis SSO), c'est une connexion directe
    if (username && typeof username === 'object' && username.id && password) {
      // Connexion SSO directe : username est l'objet user, password est le token
      console.log('SSO login with user:', username);
      setUser(username);
      localStorage.setItem('token', password);
      return { success: true };
    }
    
    // Connexion normale avec username/password
    console.log('Normal login attempt for:', username);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur de connexion');
      }

      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

// Composant pour gérer l'authentification SSO avec accès à useLocation
function AuthHandler({ children }) {
  const { setUser, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessingSSO, setIsProcessingSSO] = useState(false);
  const hasProcessedSSO = useRef(false);
  
  useEffect(() => {
    const handleSSO = async () => {
      const urlParams = new URLSearchParams(location.search);
      const ssoToken = urlParams.get('sso_token');
      
      // Ne traiter que si : token présent, pas déjà traité, et pas déjà authentifié
      if (ssoToken && !hasProcessedSSO.current && !user) {
        console.log('🚀 SSO token detected - Processing immediately...');
        hasProcessedSSO.current = true; // Empêcher les traitements multiples
        setIsProcessingSSO(true); // Afficher le loading
        
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          // FORCE utilisation de /api/auth/sso pour compatibilité Coolify
          const apiUrl = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
          const fullSSOUrl = `${apiUrl}/auth/sso?sso_token=${encodeURIComponent(ssoToken)}`;
          
          console.log('🔄 Calling SSO endpoint:', fullSSOUrl.substring(0, 80) + '...');
          
          const response = await fetch(fullSSOUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          console.log('📡 SSO Response status:', response.status);

          if (response.ok) {
            const contentType = response.headers.get('content-type');
            console.log('📄 Content-Type:', contentType);
            
            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              console.log('✅ SSO Success! Auto-login for:', data.user.username);
              console.log('👤 User role:', data.user.role, '| Company:', data.user.company);
              
              // Connexion instantanée
              localStorage.setItem('token', data.access_token);
              setUser(data.user);
              
              // Nettoyer l'URL immédiatement
              const cleanUrl = window.location.origin + location.pathname;
              window.history.replaceState({}, document.title, cleanUrl);
              
              console.log('🏠 Redirecting to dashboard...');
              
              // Redirection immédiate selon le rôle
              let targetRoute = '/manager'; // Fallback
              if (data.user.role === 'rh') {
                targetRoute = '/rh';
              } else if (data.user.role === 'manager') {
                if (data.user.company === 'Horizon Solution') {
                  targetRoute = '/manager-horizon';
                } else {
                  targetRoute = '/manager';
                }
              } else if (data.user.role === 'admin') {
                targetRoute = '/admin';
              }
              
              console.log('🎯 Navigating to:', targetRoute);
              
              // Désactiver le spinner juste avant la navigation
              setIsProcessingSSO(false);
              
              // Naviguer avec React Router
              navigate(targetRoute, { replace: true });
            } else {
              console.error('❌ SSO endpoint returned HTML, not JSON');
              setIsProcessingSSO(false);
              hasProcessedSSO.current = false;
              // Nettoyer l'URL et laisser l'utilisateur sur login
              const cleanUrl = window.location.origin + location.pathname;
              window.history.replaceState({}, document.title, cleanUrl);
            }
            
          } else {
            console.error('❌ SSO failed with status:', response.status);
            const errorText = await response.text();
            console.error('❌ Error details:', errorText);
            setIsProcessingSSO(false);
            hasProcessedSSO.current = false;
            const cleanUrl = window.location.origin + location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
          
        } catch (error) {
          console.error('❌ SSO error:', error);
          console.error('❌ Error stack:', error.stack);
          setIsProcessingSSO(false);
          hasProcessedSSO.current = false;
          const cleanUrl = window.location.origin + location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    };

    handleSSO();
  }, [location, setUser, navigate, user]);
  
  // Si on traite un token SSO, afficher un loading élégant
  if (isProcessingSSO) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #f76f4b 0%, #ff9068 100%)',
        color: 'white',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', fontWeight: '300' }}>
          Connexion en cours...
        </h2>
        <p style={{ margin: '0', fontSize: '16px', opacity: '0.8' }}>
          Chargement de votre espace Recrutement
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }
  
  return children;
}

// Main App Component
function App() {
  return (
    <Router>
      <AuthProvider>
        <AuthHandler>
          <div className="App">
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route 
              path="/rh" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <RHHomepage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerHomepage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager-horizon" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerHorizonHomepage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/recrutements-generaux" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <RecrutementsGeneraux />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <JobOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/vivier-candidatures" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <VivierCandidatures />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/offres-archivees" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <RHOffresArchivees />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/archived-offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <RHArchivedOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/horizon-solution" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <RHHorizonSolution />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/hs-offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="rh">
                  <RHHSOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rh/gestion-comptes" 
              element={
                <ProtectedRoute requiredRole={["rh", "admin"]}>
                  <GestionComptes />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminHomepage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/offres-en-cours" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOffresEnCours />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/offres-en-attente" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOffresEnAttente />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/offres-archivees" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOffresArchivees />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/archived-offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminArchivedOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/offres-en-cours" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerOffresEnCours />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/offres-en-attente" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerOffresEnAttente />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole={["manager", "rh"]}>
                  <ManagerOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/offres-archivees" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerOffresArchivees />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/archived-offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerArchivedOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager-hs/offres-en-cours" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerHSOffresEnCours />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager-hs/offres-en-attente" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerHSOffresEnAttente />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager-hs/offer-detail/:offerId" 
              element={
                <ProtectedRoute requiredRole="manager">
                  <ManagerHSOfferDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/" 
              element={<Navigate to="/login" replace />} 
            />
            <Route 
              path="/unauthorized" 
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl text-destructive mb-4">Accès non autorisé</h1>
                    <p>Vous n&apos;avez pas les permissions nécessaires pour accéder à cette page.</p>
                  </div>
                </div>
              } 
            />
            </Routes>
            <Toaster />
          </div>
        </AuthHandler>
      </AuthProvider>
    </Router>
  );
}

export default App;